import { ProtectionInfoDetectionType, ProtectionInfoDTO } from 'api-models'
import {
	regionFromCodes,
	regionToCodes
} from 'components/country-select/region-selection'
import {
	cloneSettings,
	DEFAULT_SETTINGS,
	ProtectionSettings
} from './protection-values'
import { SIGNALS } from './signals'

/**
 * `ProtectionSettings` ↔ `ProtectionInfoDTO` (DEV-2422 §3.1).
 *
 * ⚠️ `patchProtectionInfo` REPLACES the stored object, it does not merge
 * (`asset-service.ts:191`, `ad-account-service.ts:129`, and the campaign twins). Every
 * field omitted from the payload becomes `false` / `[]`, which is why `toPayload` takes
 * the previous DTO and echoes back the fields with no UI.
 */

/**
 * The whitelist is stored as an array of entries, one address or CIDR range each —
 * that is what `ip-whitelist-analyzer.ts:11` iterates and validates per entry.
 *
 * The old page wrote the whole textarea as ONE element
 * (`protection-checks.tsx:163`, `ip_whitelist: [e.target.value]`), so a multi-line list
 * was stored as a single newline-joined blob that never matched anything. Split on read
 * as well as write, or those rows stay broken.
 */
const parseWhitelist = (stored: Array<string> | null | undefined): string[] =>
	(stored ?? [])
		.flatMap((entry) => entry.split('\n'))
		.map((entry) => entry.trim())
		.filter(Boolean)

export const whitelistToLines = (whitelist: string): string[] =>
	whitelist
		.split('\n')
		.map((entry) => entry.trim())
		.filter(Boolean)

/**
 * Does this entity carry its OWN protection settings, or is it following its parent?
 * (DEV-2422 §2.6.)
 *
 * The object has to EXIST and say it does not inherit — the exact test the engine makes
 * (`core/lib/utils/protection.ts:64,68,77`) and the admin page with it. Both halves
 * matter, and for different reasons:
 *
 * - `protection` guards the created state: an ad account starts at `protection: null`
 *   and a campaign with no key at all, which a bare `!inherit` would read as owning.
 * - `!inherit`, NOT `=== false`, covers a settings object stored before `inherit`
 *   existed. The engine applies that account's own settings; `=== false` called it
 *   inheriting, so the panel omitted it, both drawers opened with the box ticked
 *   showing the WEBSITE's values, and the next save wrote those over its live ones.
 *
 * One function because the answer has to be identical in four places — the own-settings
 * panel, both drawers and the parent-chain resolution — and two of them spelling it
 * differently is how they drift.
 */
export const ownsProtection = (entity: {
	protection?: ProtectionInfoDTO | null
}): boolean => !!entity.protection && !entity.protection.inherit

/** The complement, for the drawers' inherit checkbox. */
export const inheritsProtection = (entity: {
	protection?: ProtectionInfoDTO | null
}): boolean => !ownsProtection(entity)

export const protectionToSettings = (
	protection: ProtectionInfoDTO | null | undefined
): ProtectionSettings => {
	if (!protection) return cloneSettings(DEFAULT_SETTINGS)

	const whitelist = parseWhitelist(protection.ip_whitelist)

	return {
		aiEnabled: !!protection.enabled,
		// Never persisted; the checkbox always opens unticked (§1.1).
		keepTracking: false,
		region: regionFromCodes(protection.region),
		// `disrupt` is dropped from the UI and from the accepted values, and the one-off
		// backfill moves stored rows to `block`. Until it has run, read one as Block.
		protectionLevel:
			protection.blocking_mode === 'monitoring' ? 'monitoring' : 'full',
		sensitivityMode:
			protection.detection_type === ProtectionInfoDetectionType.CUSTOM_TUNING
				? 'custom'
				: 'automatic',
		aggressiveness: protection.mode ?? 3,
		checks: SIGNALS.map((signal) => ({
			id: signal.id,
			enabled: !!protection[signal.id]
		})),
		// Derived, exactly as `getProtectionChecksValues:63` derives it today.
		whitelistOn: whitelist.length > 0,
		whitelist: whitelist.join('\n')
	}
}

export const settingsToProtection = (
	settings: ProtectionSettings,
	previous: ProtectionInfoDTO | null | undefined,
	options: {
		inherit?: boolean
		/** `useSignalCoverage`. Required rather than optional so a new call site cannot
		 *  silently skip the mask below — see the note on it. */
		covers: (feature?: string) => boolean
	}
): ProtectionInfoDTO => {
	const checks = Object.fromEntries(
		SIGNALS.map((signal) => [
			signal.id,
			// The USER'S stored value, never the forced-on display value Automatic paints.
			// Switching modes twice must not cost the user their set, and sending a gated
			// signal as `true` would trip the API's 403 (`assets-api.ts:688-700`).
			//
			// Masked by plan coverage for the same reason the card renders those rows off
			// and disabled (`unavailable`): a website created during a trial stores every
			// signal the trial "covers" (`asset-service.ts:112`, and `hasFeature` is true
			// for every trial), so after it converts to a lower tier that stored `true` is
			// unreachable on screen and 403s EVERY save — a whitelist edit included —
			// until `alignAccount` rewrites it at the next renewal.
			options.covers(signal.feature) &&
				!!settings.checks.find((check) => check.id === signal.id)?.enabled
		])
	) as Pick<ProtectionInfoDTO, (typeof SIGNALS)[number]['id']>

	const payload: ProtectionInfoDTO = {
		...checks,
		// Forced on the inherit branch: `findProtectionConfig` reads `enabled` BEFORE
		// `inherit` (`core/lib/utils/protection.ts:63,76`), so `{ enabled: false,
		// inherit: true }` is permanently unprotected and no screen shows it.
		enabled: options.inherit ? true : settings.aiEnabled,
		detection_type:
			settings.sensitivityMode === 'custom'
				? ProtectionInfoDetectionType.CUSTOM_TUNING
				: ProtectionInfoDetectionType.AI,
		mode: settings.aggressiveness as ProtectionInfoDTO['mode'],
		blocking_mode:
			settings.protectionLevel === 'monitoring' ? 'monitoring' : 'block',
		region: regionToCodes(settings.region),
		region_mode: previous?.region_mode ?? null,
		// No UI, and never had one — but both validators require it and the patch replaces
		// the whole object, so dropping it would silently flip a stored setting to false.
		vpn: previous?.vpn ?? false,
		ip_whitelist: settings.whitelistOn
			? whitelistToLines(settings.whitelist)
			: []
	}

	if (previous?.whitelist_data_center_traffic !== undefined) {
		payload.whitelist_data_center_traffic =
			previous.whitelist_data_center_traffic
	}
	// Assets never carry `inherit`; the ad-account and campaign drawers always send it.
	if (options.inherit !== undefined) payload.inherit = options.inherit

	return payload
}
